// src/pages/Users/UserList.jsx
// -----------------------------------------------------------------------------
// Admin screen to manage system users (Requirement #4, Requirement #24).
// Full CRUD operations built with TableList, UserForm, DeleteConfirmModal,
// and custom hook useUserData.
// -----------------------------------------------------------------------------
import { useState, useEffect, useMemo } from 'react';
import useUserData from '../../hooks/useUserData';
import TableList from '../../components/common/TableList';
import UserForm from './UserForm';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';
import StatusBadge from '../../components/common/StatusBadge';
import roleService from '../../api/roleService';
import clientOrgService from '../../api/clientOrgService';

export default function UserList() {
  // Initialize custom CRUD Hook for Users pointing to real backend /Auth/get-all
  const {
    data,
    paginatedData,
    loading,
    submitting,
    deleting,
    serverErrors,
    searchTerm,
    setSearchTerm,
    sortConfig,
    handleSort,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalRecords,
    isFormOpen,
    isDeleteOpen,
    formMode,
    selectedRecord,
    openCreateModal,
    openEditModal,
    closeFormModal,
    openDeleteModal,
    closeDeleteModal,
    handleSave,
    handleDelete,
  } = useUserData();

  const [roles, setRoles] = useState([]);
  const [clientOrgs, setClientOrgs] = useState([]);

  useEffect(() => {
    roleService.getAll()
      .then((res) => {
        setRoles(res?.data || []);
      })
      .catch((err) => {
        console.error("Failed to fetch roles:", err);
      });

    clientOrgService.getAll()
      .then((res) => {
        setClientOrgs(res?.data || []);
      })
      .catch((err) => {
        console.error("Failed to fetch client organizations:", err);
      });
  }, []);

  // Dynamic fields configuration matching the real Auth register DTO schema
  const userFields = useMemo(() => {
    const isEditMode = formMode === 'edit';

    const fields = [
      {
        name: 'first_Name',
        label: 'First Name',
        type: 'varchar',
        required: true,
        maxLength: 50,
        placeholder: 'e.g. John',
      },
      {
        name: 'last_Name',
        label: 'Last Name',
        type: 'varchar',
        required: true,
        maxLength: 50,
        placeholder: 'e.g. Doe',
      },
      {
        name: 'email',
        label: 'Email',
        type: 'email',
        required: true,
        maxLength: 100,
        placeholder: 'e.g. john.doe@company.com',
      },
      {
        name: 'phone',
        label: 'Phone',
        type: 'phone',
        maxLength: 20,
        placeholder: 'e.g. 8975850938',
      },
      {
        name: 'role_ID',
        label: 'Role',
        type: 'select',
        required: true,
        options: roles.map((r) => ({ label: r.roleName, value: r.roleID })),
      },
      {
        name: 'client_Org_ID',
        label: 'Client Organization',
        type: 'select',
        options: [
          { label: 'None', value: '' },
          ...clientOrgs.map((c) => ({ label: c.orgName, value: c.clientOrgID })),
        ],
      },
    ];

    if (isEditMode) {
      fields.unshift({
        name: 'id',
        label: 'User ID',
        type: 'integer',
        isPrimaryKey: true,
        readOnly: true,
      });
    }

    return fields;
  }, [formMode, roles, clientOrgs]);

  // Data grid columns mapping to API response DTO keys
  const columns = [
    { key: 'id', label: 'User ID', sortable: true, width: '100px' },
    {
      key: 'name',
      label: 'Full Name',
      sortable: true,
      render: (row) => `${row.first_Name || ''} ${row.last_Name || ''}`.trim() || '-',
    },
    { key: 'email', label: 'Email', sortable: true },
    {
      key: 'phone',
      label: 'Phone',
      sortable: true,
      render: (row) => row.phone || row.phone_1 || '-',
    },
    {
      key: 'role',
      label: 'Role',
      sortable: true,
      render: (row) => {
        const found = roles.find((r) => Number(r.roleID) === Number(row.role_ID) || Number(r.roleID) === Number(row.roleID));
        return found ? found.roleName : (row.role || row.role_ID || '-');
      }
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => (
        <StatusBadge
          value={
            row.status ||
            row.Status ||
            row.userStatus ||
            row.statusName ||
            (row.isActive !== undefined ? (row.isActive ? 'Active' : 'Inactive') : '')
          }
        />
      ),
    },
  ];

  const handleFormSubmit = async (formData) => {
    const mappedData = {
      id: formMode === 'edit' ? Number(selectedRecord.id) : 0,
      username: formData.email,
      email: formData.email,
      first_Name: formData.first_Name,
      last_Name: formData.last_Name,
      phone: formData.phone || '',
      role_ID: formData.role_ID ? Number(formData.role_ID) : 0,
      client_Org_ID: formData.client_Org_ID ? Number(formData.client_Org_ID) : null,
    };
    await handleSave(mappedData);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Users</h1>
          <div className="subtitle">
            Manage system operators, role configurations, and address profiles.
          </div>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          + Add User
        </button>
      </div>

      <TableList
        columns={columns}
        data={data}
        paginatedData={paginatedData}
        loading={loading}
        idKey="id"
        emptyMessage="No users found."
        // Search & Filter
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        // Sorting
        sortConfig={sortConfig}
        onSort={handleSort}
        // Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        // Actions
        onEdit={openEditModal}
        onDelete={openDeleteModal}
      />

      {/* Conditionally mount form to ensure state resets */}
      {isFormOpen && (
        <UserForm
          fields={userFields}
          initialValues={
            selectedRecord
              ? {
                  ...selectedRecord,
                  phone: selectedRecord.phone || '',
                  role_ID: selectedRecord.role_ID ?? selectedRecord.roleID ?? '',
                  client_Org_ID: selectedRecord.client_Org_ID ?? selectedRecord.clientOrgID ?? '',
                }
              : {
                  first_Name: '',
                  last_Name: '',
                  email: '',
                  phone: '',
                  role_ID: '',
                  client_Org_ID: '',
                }
          }
          mode={formMode}
          isOpen={isFormOpen}
          onSubmit={handleFormSubmit}
          onCancel={closeFormModal}
          submitting={submitting}
          serverErrors={serverErrors}
          itemName="User"
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete User"
        itemLabel={selectedRecord ? `${selectedRecord.first_Name} ${selectedRecord.last_Name}` : 'User'}
        deleting={deleting}
      />
    </div>
  );
}